// Macros contra o WootDash — substitui o n8n (Switch com 60+ ramos + SQL solto).
// Diferença real de arquitetura, mantida de propósito (nossa arquitetura está correta):
// cada ação é um dos 15 tipos nomeados que o backend sabe executar (MacroActionType),
// não um par genérico {action_name, action_params}. O editor da tela reflete isso — um
// tipo por ação, com os campos certos pra cada um, não um formulário de chave/valor livre.
// Editar/Excluir agora têm endpoint real (PUT/DELETE /macros/{id} — excluir é soft, via
// Macro.IsActive=false, mesmo padrão de Flow, então a macro some da listagem sem quebrar
// o histórico de execuções passadas). executeMacro() é usado pelo ConvDrawer portado
// (Dashboard real) — o backend roda as 15 ações server-side numa transação só e devolve
// um resultado agregado (ActionsApplied), não passo a passo por ação.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

export type MacroActionTypeKey =
  | 'send_message' | 'assign_agent' | 'change_status' | 'assign_team' | 'add_tag' | 'remove_tag'
  | 'add_private_note' | 'send_transcript_email' | 'send_webhook' | 'send_webhook_with_form'
  | 'resolve_conversation' | 'reopen_conversation' | 'postpone_conversation' | 'mute_conversation' | 'set_flow'

export const MACRO_ACTION_TYPES: { key: MacroActionTypeKey; label: string }[] = [
  { key: 'send_message', label: 'Enviar mensagem' },
  { key: 'add_private_note', label: 'Adicionar nota privada' },
  { key: 'assign_agent', label: 'Atribuir a um agente' },
  { key: 'assign_team', label: 'Atribuir a um time' },
  { key: 'set_flow', label: 'Setar Fluxo' },
  { key: 'add_tag', label: 'Adicionar etiqueta' },
  { key: 'remove_tag', label: 'Remover etiqueta' },
  { key: 'change_status', label: 'Mudar status da conversa' },
  { key: 'resolve_conversation', label: 'Resolver conversa' },
  { key: 'reopen_conversation', label: 'Reabrir conversa' },
  { key: 'postpone_conversation', label: 'Adiar conversa' },
  { key: 'mute_conversation', label: 'Silenciar conversa' },
  { key: 'send_transcript_email', label: 'Enviar transcrição por email' },
  { key: 'send_webhook', label: 'Chamar webhook' },
  { key: 'send_webhook_with_form', label: 'Chamar webhook com formulário' },
]

export interface MacroFormFieldDef { key: string; label: string }

export interface MacroActionDef {
  type: MacroActionTypeKey
  content?: string
  agentId?: string
  status?: 'Open' | 'Pending' | 'Resolved' | 'Closed'
  teamId?: string
  flowId?: string
  tagId?: string
  email?: string
  url?: string
  headers?: Record<string, string>
  bodyTemplate?: string
  fields?: MacroFormFieldDef[]
}

export interface CpMacro {
  id: string
  name: string
  description: string | null
  actions: MacroActionDef[]
  teamIds: string[]
  visibility: 'Global' | 'Private'
  isActive: boolean
}

interface RawMacroSummary {
  id: string; name: string; description: string | null; actions: string; teamIds: string
  visibility: number; isActive: boolean
}

function parseActionsJson(json: string): MacroActionDef[] {
  try {
    const parsed = JSON.parse(json)
    return (parsed?.actions ?? []).map((a: any): MacroActionDef => ({
      type: a.type, content: a.content, agentId: a.agentId, status: a.status, teamId: a.teamId,
      tagId: a.tagId, email: a.email, url: a.url, headers: a.headers, bodyTemplate: a.bodyTemplate,
      fields: a.fields, flowId: a.flowId,
    }))
  } catch {
    return []
  }
}

// MacroVisibility é serializado como número pelo System.Text.Json (Global=0, Private=1),
// não como string — confirmado testando contra macros reais já cadastradas.
function toRawSummary(m: RawMacroSummary): CpMacro {
  let teamIds: string[] = []
  try { teamIds = JSON.parse(m.teamIds) || [] } catch { teamIds = [] }
  return { id: m.id, name: m.name, description: m.description, actions: parseActionsJson(m.actions), teamIds, visibility: m.visibility === 1 ? 'Private' : 'Global', isActive: m.isActive }
}

function authHeaders(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

export async function listMacros(token: string): Promise<CpMacro[]> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/macros`, { headers: authHeaders(token) })
    if (!res.ok) return []
    const data: RawMacroSummary[] = await res.json()
    return data.map(toRawSummary)
  } catch {
    return []
  }
}

function buildActionsJson(actions: MacroActionDef[]): string {
  return JSON.stringify({
    actions: actions.map(a => ({
      type: a.type, content: a.content || undefined, agentId: a.agentId || undefined,
      status: a.status || undefined, teamId: a.teamId || undefined, tagId: a.tagId || undefined,
      email: a.email || undefined, url: a.url || undefined,
      headers: a.headers && Object.keys(a.headers).length ? a.headers : undefined,
      bodyTemplate: a.bodyTemplate || undefined, fields: a.fields && a.fields.length ? a.fields : undefined,
      flowId: a.flowId || undefined,
    })),
  })
}

export async function createMacro(token: string, name: string, description: string, actions: MacroActionDef[], teamIds: string[]): Promise<{ ok: boolean; macro?: CpMacro }> {
  const res = await fetch(`${WOOTDASH_BASE}/macros`, {
    method: 'POST', headers: authHeaders(token, true),
    body: JSON.stringify({ name, description: description || null, actions: buildActionsJson(actions), teamIds }),
  })
  if (!res.ok) return { ok: false }
  const raw: RawMacroSummary = await res.json()
  return { ok: true, macro: toRawSummary(raw) }
}

export async function updateMacro(
  token: string, id: string, name: string, description: string, actions: MacroActionDef[], teamIds: string[],
): Promise<{ ok: boolean; macro?: CpMacro }> {
  const res = await fetch(`${WOOTDASH_BASE}/macros/${id}`, {
    method: 'PUT', headers: authHeaders(token, true),
    body: JSON.stringify({ name, description: description || null, actions: buildActionsJson(actions), teamIds }),
  })
  if (!res.ok) return { ok: false }
  const raw: RawMacroSummary = await res.json()
  return { ok: true, macro: toRawSummary(raw) }
}

export async function deleteMacro(token: string, id: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/macros/${id}`, { method: 'DELETE', headers: authHeaders(token) })
  return res.ok
}

export interface ExecuteMacroResult {
  success: boolean
  failureReason: string
  actionsApplied: number
}

export async function executeMacro(
  token: string, macroId: string, conversationId: string, formValues?: Record<string, string>,
): Promise<ExecuteMacroResult> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/macros/${macroId}/executar`, {
      method: 'POST', headers: authHeaders(token, true),
      body: JSON.stringify({ conversationId, formValues: formValues ?? null }),
    })
    if (!res.ok) return { success: false, failureReason: res.status === 404 ? 'NaoEncontrada' : 'Erro', actionsApplied: 0 }
    return await res.json()
  } catch {
    return { success: false, failureReason: 'Erro', actionsApplied: 0 }
  }
}
